import { useEffect, useState } from 'react';
import { CalendarClock, Plus, Trash2, Copy } from 'lucide-react';
import ModalShell from '../ui/ModalShell';
import { SECTION_COLOR, hlavicky, nacti } from './ui';

/**
 * Časovky — objednávky na konkrétní čas. Nastavení má každá rozvozová zóna
 * a zvlášť osobní odběr (`zonaId` null): okna po dnech, výjimky, délka
 * a kapacita termínu, „nejdříve za" (kolik minut před termínem musí být
 * časovka objednaná), jak daleko dopředu a „skočí do fronty" (předstih,
 * kdy časovka z kalendáře objednávek odejde do kuchyně).
 * Návrh: „Plánované objednávky a kalendář časů" (2026-10-15).
 *
 * `CasovkyKarta` je souhrn do třetího sloupce Zón (zóna / přehled), úprava
 * v modalu `CasovkyModal`.
 */
const FIALOVA = '#A78BFA';
const DNY = ['Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota', 'Neděle'];
const DELKY = [10, 15, 20, 30, 60];

const souhrn = (c) => (c?.povoleno
    ? `${c.kapacita} na ${c.delka_min} min · nejdříve za ${c.nejdrive_za_min} min · do fronty ${c.skoci_pred_min} min předem`
    : 'vypnuté');

export function CasovkyKarta({ zonaId = null, nazev, muted, cara, isDark, upravuje }) {
    const [vse, setVse] = useState(null);
    const [otevreno, setOtevreno] = useState(false);
    const obnov = () => nacti('/api/rozvoz/zony/casovky').then((d) => setVse(d.casovky)).catch(() => setVse([]));
    useEffect(() => { obnov(); }, []);
    const c = vse?.find((x) => (x.zona_id ?? null) === (zonaId ?? null));
    const oknaPopis = c?.povoleno
        ? DNY.map((d, i) => {
            const o = c.okna?.[String(i + 1)] || [];
            return o.length ? `${d.slice(0, 2)} ${o.map(([a, b]) => `${a}–${b}`).join(', ')}` : null;
        }).filter(Boolean).join(' · ') || 'žádná okna'
        : null;

    return (
        <>
            <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '16px 0 6px' }}>
                Časovky{zonaId === null ? ' · osobní odběr' : ''}
            </p>
            {!vse ? <p className="text-xs" style={{ color: muted }}>Načítám…</p> : (
                <div style={{ borderBottom: `1px solid ${cara}`, paddingBottom: 8 }}>
                    <p className="text-[13px]" style={{ margin: 0, color: c?.povoleno ? 'var(--text-primary)' : muted, fontWeight: c?.povoleno ? 600 : 400 }}>{souhrn(c)}</p>
                    {oknaPopis && <p className="text-xs" style={{ margin: '4px 0 0', color: muted }}>{oknaPopis}</p>}
                    {c?.vyjimky?.length > 0 && <p className="text-xs" style={{ margin: '2px 0 0', color: muted }}>{c.vyjimky.length} výjimek</p>}
                </div>
            )}
            {upravuje && vse && (
                <button type="button" onClick={() => setOtevreno(true)} className="inline-flex items-center gap-1.5 rounded-lg"
                    style={{ height: 34, marginTop: 8, padding: '0 12px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: 'var(--text-primary)',
                        border: `1px solid ${isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)'}` }}>
                    <CalendarClock size={13} /> Nastavit časovky
                </button>
            )}
            {otevreno && c && (
                <CasovkyModal kdo={zonaId ?? 'odber'} nazev={nazev} vychozi={c} isDark={isDark} muted={muted}
                    onZavrit={() => setOtevreno(false)} onUlozeno={() => { setOtevreno(false); obnov(); }} />
            )}
        </>
    );
}

export function CasovkyModal({ kdo, nazev, vychozi, isDark, muted, onZavrit, onUlozeno }) {
    const [c, setC] = useState(() => JSON.parse(JSON.stringify(vychozi)));
    const [uklada, setUklada] = useState(false);
    const [chyba, setChyba] = useState(null);
    const border = isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)';
    const pole = isDark ? 'rgba(255,255,255,0.04)' : '#fff';
    const nastav = (k, v) => setC((x) => ({ ...x, [k]: v }));
    const okna = (d) => c.okna?.[String(d)] || [];
    const nastavOkna = (d, o) => setC((x) => ({ ...x, okna: { ...x.okna, [String(d)]: o } }));
    const vsedniJakoPondeli = () => setC((x) => ({ ...x, okna: { ...x.okna, 2: okna(1), 3: okna(1), 4: okna(1), 5: okna(1) } }));

    const ulozit = async () => {
        setUklada(true); setChyba(null);
        const res = await fetch(`/api/rozvoz/zony/casovky/${kdo}`, { method: 'PUT', headers: hlavicky(), credentials: 'same-origin', body: JSON.stringify(c) });
        const d = await res.json().catch(() => ({}));
        setUklada(false);
        if (!res.ok) return setChyba(d?.message || d?.error || 'Uložit se nepodařilo.');
        onUlozeno?.(d.casovky);
    };

    const cislo = (k, popis, napoveda, min, max, krok = 1, jednotka = 'min') => (
        <div className="flex items-center justify-between" style={{ gap: 12, minHeight: 44 }}>
            <div style={{ minWidth: 0 }}>
                <div className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>{popis}</div>
                <div className="text-xs" style={{ color: muted }}>{napoveda}</div>
            </div>
            <div className="flex items-center" style={{ gap: 4, flexShrink: 0 }}>
                <button type="button" aria-label={`${popis} méně`} onClick={() => nastav(k, Math.max(min, c[k] - krok))} style={krokStyl(border, pole)}>−</button>
                <span className="text-[13px] font-bold" style={{ minWidth: 64, textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>{c[k]} {jednotka}</span>
                <button type="button" aria-label={`${popis} více`} onClick={() => nastav(k, Math.min(max, c[k] + krok))} style={krokStyl(border, pole)}>+</button>
            </div>
        </div>
    );

    const casVstup = (hodnota, onZmena, popis) => (
        <input type="time" step={300} value={hodnota} aria-label={popis} onChange={(e) => onZmena(e.target.value)}
            className="rounded-md text-[13px]" style={{ height: 32, padding: '0 6px', background: pole, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
    );

    return (
        <ModalShell onClose={onZavrit} isDark={isDark} ikona={CalendarClock} barva={FIALOVA} nadpis={`Časovky · ${nazev}`} sirka={640}
            paticka={(
                <div className="flex items-center gap-2 w-full">
                    {chyba && <span className="text-xs flex-1" style={{ color: '#EF4444' }}>{chyba}</span>}
                    {!chyba && <span className="flex-1" />}
                    <button type="button" onClick={onZavrit} className="rounded-xl font-semibold" style={{ height: 42, padding: '0 16px', background: 'transparent', border: `1px solid ${border}`, color: 'var(--text-primary)', cursor: 'pointer' }}>Zrušit</button>
                    <button type="button" onClick={ulozit} disabled={uklada} className="rounded-xl font-semibold" style={{ height: 42, padding: '0 18px', background: SECTION_COLOR, color: '#0A0612', border: 'none', cursor: 'pointer', opacity: uklada ? 0.6 : 1 }}>Uložit</button>
                </div>
            )}>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <label className="flex items-center gap-2 text-[14px] font-semibold" style={{ color: 'var(--text-primary)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!c.povoleno} onChange={(e) => nastav('povoleno', e.target.checked)} style={{ width: 18, height: 18, accentColor: FIALOVA }} />
                    Časovky {kdo === 'odber' ? 'pro osobní odběr' : 'v téhle zóně'} zapnuté
                </label>

                <div style={{ opacity: c.povoleno ? 1 : 0.5, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <section>
                        <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
                            <h3 className="text-[13px] font-bold" style={{ margin: 0, color: 'var(--text-primary)' }}>Kdy jde časovku objednat</h3>
                            <button type="button" onClick={vsedniJakoPondeli} className="inline-flex items-center gap-1 text-xs font-semibold" style={{ background: 'none', border: 'none', color: SECTION_COLOR, cursor: 'pointer' }}>
                                <Copy size={12} /> Út–Pá jako pondělí
                            </button>
                        </div>
                        {DNY.map((den, i) => (
                            <div key={den} className="flex items-center flex-wrap" style={{ gap: 6, minHeight: 40, borderBottom: `1px solid ${border}`, padding: '4px 0' }}>
                                <span className="text-[13px] font-semibold" style={{ width: 76, color: 'var(--text-primary)' }}>{den}</span>
                                {okna(i + 1).length === 0 && <span className="text-xs" style={{ color: muted }}>nejde</span>}
                                {okna(i + 1).map(([od, do_], j) => (
                                    <span key={j} className="inline-flex items-center" style={{ gap: 4, padding: '2px 4px', borderRadius: 8, background: 'rgba(167,139,250,0.12)' }}>
                                        {casVstup(od, (v) => nastavOkna(i + 1, okna(i + 1).map((o, k) => (k === j ? [v, o[1]] : o))), `${den} od`)}
                                        <span className="text-xs" style={{ color: muted }}>–</span>
                                        {casVstup(do_, (v) => nastavOkna(i + 1, okna(i + 1).map((o, k) => (k === j ? [o[0], v] : o))), `${den} do`)}
                                        <button type="button" aria-label="Odebrat okno" onClick={() => nastavOkna(i + 1, okna(i + 1).filter((_, k) => k !== j))} style={{ background: 'none', border: 'none', color: muted, cursor: 'pointer', padding: 2 }}><Trash2 size={13} /></button>
                                    </span>
                                ))}
                                <button type="button" aria-label={`Přidat okno ${den}`} onClick={() => nastavOkna(i + 1, [...okna(i + 1), okna(i + 1).length ? ['17:00', '21:00'] : ['11:00', '14:00']])}
                                    style={{ width: 30, height: 30, borderRadius: 8, border: `1px dashed ${border}`, background: 'transparent', color: muted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><Plus size={14} /></button>
                            </div>
                        ))}
                    </section>

                    <section>
                        <h3 className="text-[13px] font-bold" style={{ margin: '0 0 6px', color: 'var(--text-primary)' }}>Termíny</h3>
                        <div className="flex items-center justify-between" style={{ gap: 12, minHeight: 44 }}>
                            <div>
                                <div className="text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>Délka termínu</div>
                                <div className="text-xs" style={{ color: muted }}>mřížka na pokladně, např. 11:00, 11:15…</div>
                            </div>
                            <div role="group" aria-label="Délka termínu" className="flex" style={{ gap: 2, padding: 3, borderRadius: 10, border: `1px solid ${border}` }}>
                                {DELKY.map((d) => (
                                    <button key={d} type="button" aria-pressed={c.delka_min === d} onClick={() => nastav('delka_min', d)} className="text-xs font-semibold rounded-md"
                                        style={{ height: 28, padding: '0 9px', border: 'none', cursor: 'pointer', background: c.delka_min === d ? (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.07)') : 'transparent', color: c.delka_min === d ? 'var(--text-primary)' : muted }}>{d}</button>
                                ))}
                            </div>
                        </div>
                        {cislo('kapacita', 'Kapacita termínu', 'kolik časovek se vejde do jednoho termínu', 1, 99, 1, '')}
                        {cislo('nejdrive_za_min', 'Nejdříve za', 'časovku jde objednat nejméně tolik min před termínem', 0, 1440, 15)}
                        {cislo('skoci_pred_min', 'Skočí do fronty', 'kolik min před termínem odejde do kuchyně', 0, 240, 5)}
                        {cislo('dopredu_dni', 'Jak daleko dopředu', 'další dny pokladna nenabídne', 0, 60, 1, 'dní')}
                        {c.skoci_pred_min > c.nejdrive_za_min && (
                            <p className="text-xs" style={{ margin: '4px 0 0', color: '#F59E0B' }}>„Skočí do fronty“ je delší než „Nejdříve za“ — nejbližší časovky půjdou do kuchyně hned.</p>
                        )}
                    </section>

                    <section>
                        <h3 className="text-[13px] font-bold" style={{ margin: '0 0 6px', color: 'var(--text-primary)' }}>Výjimky</h3>
                        {(c.vyjimky || []).map((v, i) => (
                            <div key={i} className="flex items-center flex-wrap" style={{ gap: 8, padding: '6px 0', borderBottom: `1px solid ${border}` }}>
                                <input type="date" value={v.datum} aria-label="Datum výjimky" onChange={(e) => nastav('vyjimky', c.vyjimky.map((x, k) => (k === i ? { ...x, datum: e.target.value } : x)))}
                                    className="rounded-md text-[13px]" style={{ height: 32, padding: '0 6px', background: pole, border: `1px solid ${border}`, color: 'var(--text-primary)' }} />
                                <label className="flex items-center gap-1.5 text-[13px]" style={{ color: 'var(--text-primary)' }}>
                                    <input type="checkbox" checked={!!v.zavreno} onChange={(e) => nastav('vyjimky', c.vyjimky.map((x, k) => (k === i ? { ...x, zavreno: e.target.checked } : x)))} /> zavřeno
                                </label>
                                {!v.zavreno && (
                                    <span className="inline-flex items-center" style={{ gap: 4 }}>
                                        {casVstup(v.okna?.[0]?.[0] || '11:00', (t) => nastav('vyjimky', c.vyjimky.map((x, k) => (k === i ? { ...x, okna: [[t, x.okna?.[0]?.[1] || '15:00']] } : x))), 'Výjimka od')}
                                        <span className="text-xs" style={{ color: muted }}>–</span>
                                        {casVstup(v.okna?.[0]?.[1] || '15:00', (t) => nastav('vyjimky', c.vyjimky.map((x, k) => (k === i ? { ...x, okna: [[x.okna?.[0]?.[0] || '11:00', t]] } : x))), 'Výjimka do')}
                                    </span>
                                )}
                                <button type="button" aria-label="Odebrat výjimku" onClick={() => nastav('vyjimky', c.vyjimky.filter((_, k) => k !== i))} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: muted, cursor: 'pointer' }}><Trash2 size={14} /></button>
                            </div>
                        ))}
                        <button type="button" onClick={() => nastav('vyjimky', [...(c.vyjimky || []), { datum: new Date().toISOString().slice(0, 10), zavreno: true, okna: [] }])}
                            className="inline-flex items-center gap-1 text-xs font-semibold" style={{ marginTop: 8, background: 'none', border: 'none', color: SECTION_COLOR, cursor: 'pointer', padding: 0 }}>
                            <Plus size={12} /> Přidat výjimku
                        </button>
                    </section>
                </div>
            </div>
        </ModalShell>
    );
}

const krokStyl = (border, pole) => ({
    width: 32, height: 32, borderRadius: 8, border: `1px solid ${border}`, background: pole, color: 'var(--text-primary)', cursor: 'pointer', fontSize: 16,
});
